import {
  ForwardedRef,
  forwardRef,
  ReactNode,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import { Controller, useForm } from "react-hook-form";

import {
  checkHasResponse,
  isDigitalSignatureField,
  SubmissionSyncStatus,
} from "@smart/bridge-types-basic";
import { StaffDetails } from "@smart/bridge-types-basic/src/appointment";
import { Loading, UploadFileProps } from "@smart/itops-sb-design-system-dom";
import {
  entriesOf,
  fromEntries,
  isNotNullOrUndefined,
} from "@smart/itops-utils-basic";

import {
  fieldName,
  fieldValidation,
  getRequiredMessageKey,
  renderOrderedItems,
  useLoadMatterFields,
  useOrderedErrors,
  useOrderedItems,
  Visibility,
} from "../../hooks";
import {
  AutoFillStatus,
  ConnectionItem,
  FieldItem,
  GroupItem,
  IntakeFormSectionRef,
  LoadResponses,
  LookupOptions,
  ResponseSubmitter,
  SectionItem,
  SubmissionItem,
  TeamItem,
  Toast,
} from "../../types";
import { getTranslation, getValidationMessage } from "../../utils";
import { IntakeField } from "../field";
import {
  FieldComponentOverrideProps,
  FieldProps,
  FieldsList,
} from "../field-components";
import { IntakeGroup, IntakeGroupFieldSet } from "../group";
import { IntakeSection } from "../section";

export type IntakeFormSectionProps = {
  connection?: ConnectionItem;
  autoFillSection?: SectionItem;
  visibleSections: SectionItem[];
  selected: SectionItem | undefined;
  groups: GroupItem[];
  fields: FieldItem[];
  onSelect: (section: SectionItem) => void;
  onSummary: () => void;
  loadResponses: LoadResponses;
  submitResponses: ResponseSubmitter;
  setResponses: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  completeFormOrRedirect: (
    status: SubmissionItem["status"],
    signingUrl?: string,
  ) => void;
  beginDigitalSignatureWait: () => void;
  attemptDigitalSignatureRecovery: () => Promise<void>;
  setStatus: (status: SubmissionItem["status"]) => void;
  team: TeamItem;
  sidebar: boolean;
  setSidebar: (sidebar: boolean) => void;
  responses: Record<string, any>;
  visibility: Visibility;
  disabled?: boolean;
  isPreview?: boolean;
  showPaymentPlaceholder?: boolean;
  previewOptions?: {
    previewErrorToggle?: [
      boolean,
      React.Dispatch<React.SetStateAction<boolean>>,
    ];
    previewResponses?: [
      Record<string, any>,
      React.Dispatch<React.SetStateAction<Record<string, any>>>,
    ];
  };
  isUpdating?: boolean;
  setVisited: (section: SectionItem) => void;
  staffDetails: StaffDetails[];
  submissionSyncStatus?: SubmissionSyncStatus | null;
  autoFillStatus?: AutoFillStatus;
  loading?: boolean;
  aiFillFromMatterInfo?: boolean;
  fieldComponentOverride?: (
    fieldProps: FieldProps & FieldComponentOverrideProps,
  ) => React.ReactNode | undefined;
  setToasts?: (toasts: Toast[]) => void;
  isTeamHidden?: boolean;
  isWebEnquiry?: boolean;
  setFields?: (setFn: (fields: FieldItem[]) => FieldItem[]) => void;
  isPrint?: boolean;
  selectedLanguage?: string;
  setSelectedLanguage?: (language: string) => void;
  languageOptions?: Array<{ label: string; value: string }>;
  voiceConversation?: ReactNode;
  isMobile?: boolean;
  disableSignatureEditing?: boolean;
} & LookupOptions & { uploadFileProps: UploadFileProps };

export const IntakeFormSection = forwardRef(
  (
    {
      addressLookup,
      fileLookup,
      connection,
      autoFillSection,
      visibleSections,
      selected,
      groups,
      fields,
      onSelect,
      onSummary,
      loadResponses,
      submitResponses,
      setResponses,
      completeFormOrRedirect,
      beginDigitalSignatureWait,
      attemptDigitalSignatureRecovery,
      setStatus,
      team,
      sidebar,
      setSidebar,
      responses,
      visibility,
      disabled,
      previewOptions,
      isUpdating,
      setVisited,
      uploadFileProps,
      staffDetails,
      submissionSyncStatus,
      autoFillStatus,
      loading,
      aiFillFromMatterInfo,
      isPreview,
      showPaymentPlaceholder,
      fieldComponentOverride,
      setToasts,
      isTeamHidden,
      isWebEnquiry,
      setFields,
      isPrint,
      selectedLanguage,
      setSelectedLanguage,
      languageOptions,
      voiceConversation,
      isMobile,
      disableSignatureEditing,
    }: IntakeFormSectionProps,
    forwardedRef: ForwardedRef<IntakeFormSectionRef>,
  ) => {
    const orderedItems = useOrderedItems({
      selected,
      groups,
      fields,
      visibility,
      responses,
    });

    const form = useForm({
      defaultValues: previewOptions?.previewResponses?.[0] || responses,
    });

    // For Safari only
    const scrollPositionRef = useRef<{
      scrollContainer: HTMLElement | undefined;
      scrollPosition: number | undefined;
    }>({ scrollContainer: undefined, scrollPosition: undefined });
    const errorsRef = useRef<HTMLDivElement>(null);
    const hideErrors = previewOptions?.previewErrorToggle?.[0];
    const hasDigitalSignature = fields.some(
      (f) => isDigitalSignatureField(f) && visibility.visibleFields[f.uri],
    );
    const showNext = () => {
      if (connection?.status === "error" && !hideErrors) return;

      const selectedIndex = selected
        ? visibleSections.findIndex((s) => s.uri === selected?.uri)
        : -1;
      if (selectedIndex !== -1) setVisited(visibleSections[selectedIndex]);

      if (visibleSections.length === 1) setStatus("completed");
      const next = visibleSections[selectedIndex + 1];
      if (next) {
        onSelect(next);
      } else {
        onSummary();
      }
    };

    const onSubmit = (options?: { nextAction?: () => void; skip?: boolean }) =>
      options?.skip
        ? options.nextAction || (() => {})
        : form.handleSubmit(
            async (values) => {
              if (visibleSections.length === 1) {
                const status = "completed";
                if (hasDigitalSignature) beginDigitalSignatureWait();
                try {
                  const result = await submitResponses({
                    responses: values,
                    status,
                  });
                  if (result?.error || !result) {
                    if (hasDigitalSignature) {
                      attemptDigitalSignatureRecovery().catch(console.error);
                    } else {
                      setToasts?.([
                        {
                          text:
                            result?.error ||
                            "Something went wrong submitting the form. Please try again.",
                          palette: "destructive",
                        },
                      ]);
                    }
                  } else if (result?.operationIds) {
                    setResponses((current) => ({ ...current, ...values }));
                    completeFormOrRedirect(
                      status,
                      result?.digitalSignature?.signingUrl,
                    );
                  }
                } catch {
                  if (hasDigitalSignature) {
                    attemptDigitalSignatureRecovery().catch(console.error);
                  }
                }
                setVisited(visibleSections[0]);
              } else if (options?.nextAction) {
                options.nextAction();
              }
            },
            (errors) => {
              if (entriesOf(errors).length > 1) {
                setTimeout(() => errorsRef?.current?.focus(), 5);
              }
            },
          );

    const hasRequired = orderedItems.some((i) =>
      i.type === "field"
        ? i.field.mandatory
        : i.fields.some((f) => f.mandatory),
    );
    const errors = useOrderedErrors(
      { orderedItems },
      form.formState,
      selectedLanguage,
    );
    const hasDynamicOptions = useMemo(
      () => fields.some((f) => f.hasDynamicOptions),
      [fields],
    );

    useLoadMatterFields(
      {
        submissionSyncStatus,
        fn: () => {
          entriesOf(responses).forEach(([fieldUri, value]) => {
            const currentValue = form.getValues(fieldUri);
            if (
              !currentValue ||
              (Array.isArray(currentValue) &&
                !currentValue.filter(Boolean).length)
            ) {
              const field = fields.find((f) => f.uri === fieldUri);
              if (!field) return;

              const isGroup = !!field.groupUri;
              if (Array.isArray(value) && isGroup) {
                value.forEach((v, index) => {
                  form.setValue(fieldName({ field, index }), v);
                });
              } else {
                form.setValue(fieldName({ field }), value);
              }
            }
          });
        },
      },
      [responses],
    );

    useEffect(() => {
      if (previewOptions?.previewResponses) {
        previewOptions.previewResponses[1]((prevPreviewResponses) => ({
          ...prevPreviewResponses,
          ...responses,
        }));
      }
    }, [responses]);

    useEffect(() => {
      if (selected?.validateOnDisplay) {
        form.trigger().catch(console.error);
      }
    }, []);

    const selectedTranslation = getTranslation(
      selected?.translations,
      selectedLanguage,
    );

    useImperativeHandle(forwardedRef, () => ({
      submitForm: onSubmit(),
      errors,
      connection,
      setFieldValue: (fieldUri: string, value: any) => {
        const field = fields.find((f) => f.uri === fieldUri);
        if (!field) return;

        const isGroup = !!field.groupUri;

        let processedValue = value;
        if (isGroup && !Array.isArray(value)) {
          processedValue = [value];
        }

        if (Array.isArray(processedValue) && isGroup) {
          processedValue.forEach((v, index) => {
            form.setValue(fieldName({ field, index }), v);
          });
        } else {
          form.setValue(fieldName({ field }), processedValue);
        }

        setResponses((current) => ({
          ...current,
          [fieldUri]: processedValue,
        }));
      },
      hasUploadingFiles: () => {
        const sectionFileFields = fields.filter(
          (f) => f.type === "file" && f.sectionUri === selected?.uri,
        );
        return sectionFileFields.some((f) => {
          const val = form.getValues(fieldName({ field: f }));
          if (!Array.isArray(val)) return false;
          const files: { uploadStatus?: string }[] = f.groupUri
            ? (val as unknown[][]).flat()
            : val;
          return files.some((v) => v.uploadStatus === "uploading");
        });
      },
    }));

    const selectedIndex = visibleSections.findIndex(
      (s) => s.uri === selected?.uri,
    );

    const showBack = () => {
      if (selectedIndex > 0) {
        onSelect(visibleSections[selectedIndex - 1]);
      } else if (selectedIndex === 0 && autoFillSection) {
        onSelect(autoFillSection);
      }
    };

    const getValidationRules = (field: FieldItem) => {
      if (disabled || field.type === "info" || isDigitalSignatureField(field)) {
        return undefined;
      }

      const optionalSpecialField =
        ["appointment", "payment"].includes(field.type) && !field.mandatory;

      return {
        required: field.mandatory
          ? getRequiredMessageKey(field.type)
          : undefined,
        validate: optionalSpecialField
          ? undefined
          : fieldValidation[field.type],
      };
    };

    const isNonRepeatableField = (field: FieldItem) => field.type === "payment";

    const autoFillInProgress = autoFillStatus === "filling";

    return (
      <>
        {form.formState.isSubmitting && <Loading />}
        <IntakeSection
          connection={connection}
          team={team}
          sidebar={sidebar}
          setSidebar={setSidebar}
          autoFillSection={autoFillSection}
          sections={visibleSections}
          selected={{
            ...selected,
            uri: selected?.uri || "",
            hasRequired,
            title: selectedTranslation?.title || selected?.title,
            description:
              selectedTranslation?.description || selected?.description,
          }}
          onSubmit={hideErrors ? showNext : onSubmit({ nextAction: showNext })}
          onPreviousButton={
            hideErrors
              ? showBack
              : onSubmit({
                  nextAction: showBack,
                  skip: !isUpdating || selectedIndex === 0,
                })
          }
          onNextButton={
            disabled ? onSubmit({ nextAction: showNext }) : undefined
          }
          onSubmitButton={
            disabled ? onSubmit({ nextAction: showNext }) : undefined
          }
          errors={errors}
          isForm={!disabled}
          errorsRef={errorsRef}
          previewErrorToggle={previewOptions?.previewErrorToggle}
          retrySubmitResponses={() => {
            const values = form.getValues();
            submitResponses({
              responses: values,
            })
              .then((result) => {
                if (result?.operationIds) {
                  setResponses((current) => ({
                    ...current,
                    ...values,
                  }));
                }
              })
              .catch(() => {});
          }}
          autoFillStatus={autoFillStatus}
          loading={loading}
          aiFillFromMatterInfo={aiFillFromMatterInfo}
          isPreview={isPreview}
          setToasts={setToasts}
          isTeamHidden={isTeamHidden}
          isWebEnquiry={isWebEnquiry}
          isPrint={isPrint}
          selectedLanguage={selectedLanguage}
          setSelectedLanguage={setSelectedLanguage}
          languageOptions={languageOptions}
          voiceConversation={voiceConversation}
          isMobile={isMobile}
          hasDigitalSignature={hasDigitalSignature}
        >
          <FieldsList>
            {renderOrderedItems({
              orderedItems,
              responses,
              renderGroup: ({
                key,
                group,
                fields: groupFields,
                length,
                children,
              }) => {
                const groupTranslation = getTranslation(
                  group.translations,
                  selectedLanguage,
                );
                return (
                  <IntakeGroup
                    key={key}
                    scrollPositionRef={scrollPositionRef}
                    selectedLanguage={selectedLanguage}
                    group={{
                      ...group,
                      label: groupTranslation?.label || group.label,
                      description:
                        groupTranslation?.description || group.description,
                      repeatPrompt:
                        groupTranslation?.repeatPrompt || group.repeatPrompt,
                    }}
                    onAdd={
                      group.repeatable && length <= (group.maxRepeat || 30)
                        ? () => {
                            if (disabled) return;

                            const field = groupFields.filter(
                              (f) => f.type !== "payment",
                            )[0];
                            if (!field) return;

                            const fieldValue = form.getValues(field.uri);
                            const toUpdate = Array.isArray(fieldValue)
                              ? fieldValue.slice(0)
                              : [null];
                            toUpdate.splice(length, 0, null);
                            const updated = {
                              [field.uri]: toUpdate,
                            };

                            form.setValue(field.uri, toUpdate);
                            submitResponses({
                              responses: updated,
                              lastUpdatedFieldUri: field.uri,
                              lastUpdatedSectionUri: selected?.uri,
                            })
                              .then((result) => {
                                if (result?.operationIds) {
                                  setResponses((current) => ({
                                    ...current,
                                    ...updated,
                                  }));
                                }
                              })
                              .catch(() => {});
                          }
                        : undefined
                    }
                  >
                    <FieldsList>{children}</FieldsList>
                  </IntakeGroup>
                );
              },
              renderGroupFieldSet: ({
                key,
                index,
                length,
                group,
                fields: groupFields,
                children,
              }) => {
                const groupTranslation = getTranslation(
                  group.translations,
                  selectedLanguage,
                );
                return (
                  <IntakeGroupFieldSet
                    key={key}
                    group={{
                      ...group,
                      label: groupTranslation?.label || group.label,
                      description:
                        groupTranslation?.description || group.description,
                      repeatPrompt:
                        groupTranslation?.repeatPrompt || group.repeatPrompt,
                    }}
                    index={index}
                    selectedLanguage={selectedLanguage}
                    onRemove={
                      group.repeatable && length > (group.minRepeat || 1)
                        ? () => {
                            if (disabled) return;

                            const updated: Record<string, any> = {};
                            for (const field of groupFields.filter(
                              (f) => !isNonRepeatableField(f),
                            )) {
                              const fieldValue = form.getValues(field.uri);
                              if (Array.isArray(fieldValue)) {
                                const toUpdate = fieldValue.slice(0);
                                toUpdate.splice(index, 1);
                                updated[field.uri] = toUpdate;
                                form.setValue(field.uri, toUpdate);
                              }
                            }

                            submitResponses({
                              responses: updated,
                              lastUpdatedFieldUri: groupFields[0].uri,
                              lastUpdatedSectionUri: selected?.uri,
                            })
                              .then((result) => {
                                if (result?.operationIds) {
                                  setResponses((current) => ({
                                    ...current,
                                    ...updated,
                                  }));
                                }
                              })
                              .catch(() => {});
                          }
                        : undefined
                    }
                  >
                    {children}
                  </IntakeGroupFieldSet>
                );
              },
              renderField: ({ key, field, group, index }) => {
                // Render only one field if it's non repeatable
                if (isNonRepeatableField(field) && index && index > 0)
                  return null;

                // Check if the field is repeatable and has in-group conditions to meet
                if (
                  group?.repeatable &&
                  !visibility.visibleRepeatableFields[field.uri]?.[index!]
                ) {
                  return null;
                }

                return (
                  <Controller
                    key={key}
                    control={form.control}
                    name={fieldName({
                      field,
                      index: isNonRepeatableField(field) ? undefined : index,
                    })}
                    rules={getValidationRules(field)}
                    render={({
                      field: { ref, value, onChange, onBlur },
                      fieldState: { error },
                    }) => {
                      const isLoadingMatterInfo = !!(
                        submissionSyncStatus === "loading" &&
                        field.layout &&
                        field.field &&
                        !checkHasResponse(field.type, value)
                      );
                      const fieldTranslation = getTranslation(
                        field.translations,
                        selectedLanguage,
                      );
                      return (
                        <IntakeField
                          key={key}
                          innerRef={ref}
                          addressLookup={addressLookup}
                          fileLookup={fileLookup}
                          uploadFileProps={uploadFileProps}
                          loadResponses={loadResponses}
                          selectedLanguage={selectedLanguage}
                          field={{
                            ...field,
                            label: fieldTranslation?.label || field.label,
                            description:
                              fieldTranslation?.description ||
                              field.description,
                            options: fieldTranslation?.options || field.options,
                          }}
                          index={index}
                          value={value}
                          isPreview={isPreview}
                          showPaymentPlaceholder={showPaymentPlaceholder}
                          isPrint={isPrint}
                          error={
                            hideErrors || !error?.message
                              ? undefined
                              : getValidationMessage(
                                  error.message,
                                  selectedLanguage,
                                )
                          }
                          loading={isLoadingMatterInfo}
                          disabled={
                            disabled ||
                            isLoadingMatterInfo ||
                            autoFillInProgress
                          }
                          onBlur={() => {
                            if (disabled) return onBlur();

                            const updated = {
                              [field.uri]: form.getValues(field.uri),
                            };

                            const willBeHidden = visibility.willBeHidden(
                              updated,
                              group,
                              index,
                            );
                            const fieldMap = fromEntries(
                              fields.map((f) => [f.uri, f]),
                            );
                            for (const uri of willBeHidden) {
                              if (fieldMap[uri]?.type !== "payment") {
                                // Update responses to affecting fields within the same group
                                const fieldValue = form.getValues(uri);
                                if (fieldMap[uri].groupUri && group) {
                                  if (
                                    group.uri === fieldMap[uri].groupUri &&
                                    index !== undefined &&
                                    Array.isArray(fieldValue) &&
                                    isNotNullOrUndefined(fieldValue[index])
                                  ) {
                                    fieldValue[index] = null;
                                    updated[uri] = fieldValue;
                                    form.setValue(uri, fieldValue);
                                  }
                                } else {
                                  updated[uri] = null;
                                  form.setValue(uri, null);
                                }
                              }
                            }

                            setResponses((current) => ({
                              ...current,
                              ...updated,
                            }));

                            submitResponses({
                              responses: updated,
                              lastUpdatedFieldUri: field.uri,
                              lastUpdatedSectionUri: selected?.uri,
                              hasDynamicOptions,
                            })
                              .then((result) => {
                                if (!result?.operationIds)
                                  setResponses(responses);
                                setFields?.((current) =>
                                  result?.fieldsWithDynamicOptions
                                    ? current.map((existingField) => {
                                        const fieldWithDynamicOptions =
                                          result.fieldsWithDynamicOptions?.find(
                                            (f) => f.uri === existingField.uri,
                                          );
                                        return (
                                          fieldWithDynamicOptions ||
                                          existingField
                                        );
                                      })
                                    : current,
                                );
                              })
                              .catch(() => {
                                setResponses(responses);
                              });

                            return onBlur();
                          }}
                          onChange={disabled ? () => {} : onChange}
                          staffDetails={staffDetails}
                          fieldComponentOverride={fieldComponentOverride}
                          groups={groups}
                          fields={fields}
                          visibility={visibility}
                          submitResponses={submitResponses}
                          responses={responses}
                          setResponses={setResponses}
                          setFormValue={form.setValue}
                          disableSignatureEditing={disableSignatureEditing}
                        />
                      );
                    }}
                  />
                );
              },
            })}
          </FieldsList>
        </IntakeSection>
      </>
    );
  },
);
